import hidInputResponseTimeouts from '../../consts/hid/hidInputResponseTimeouts';
import * as firmwareErrorCodes from '../../consts/iot/firmwareErrorCodes';
import * as firmwareSteps from '../../consts/iot/firmwareSteps';
import helpers from '../../utils/helpers';
import log from '../logger/log';
import BaseFwuClient from './baseFwuClient';
import framesProcessingService from './framesProcessingService';
import fwuErrorDetailsService from './fwuErrorDetailsService';

let instance = null;

export default class SblFwuClient extends BaseFwuClient {
    constructor(options) {
        if (options && instance) {
            instance.stop();
            instance = null;
        }

        if (instance) {
            return instance;
        }

        super({fwuPackageData: options.fwuPackageData, fwu_target: options.fwu_target});
        instance = this;
        this.currentFwuTry = 0;
        this.currentBlockIndex = 0;
        this.onFwuError = options.onFwuError;
        this.onFwuStatusEvent = options.onFwuStatusEvent;

        this.pollingTimeout = null;
        this.pollingTryNumber = 0;
        this.currentTryNumber = 0;
        this.isStopped = true;
    }

    stop = () => {
        if (this.isStopped) return;

        this.isStopped = true;
        this.onFwuStopBase();
        clearTimeout(this.pollingTimeout);
    };

    isMaxTriesExceeded = () => {
        const FWU_MAX_TRIES = 10;

        if (this.currentTryNumber < FWU_MAX_TRIES) {
            this.currentTryNumber = this.currentTryNumber + 1;

            return false;
        }

        this.onFwuFail();
        return true;
    };

    startFwUpdate = (package_index) => {
        this.isStopped = false;
        log.info(`SblFwuClient: start firmware update`);

        this.setStartTs();

        framesProcessingService.restart();

        if (package_index && package_index > 0) {
            this.sendProgramBlocks(package_index);
        } else {
            if (this.fwuPackageData.suspend_master_communication?.f) {
                if (this.isFwuHolderRelatedTarget()) {
                    this.sendSuspendMasterCommunication();
                    return;
                } else {
                    //TODO: replace with warning
                    log.info(
                        'SblFwuClient: there is a suspend_master_communication frame in fwu package but target is not holder'
                    );
                }
            }

            this.sendAuthenticationOrStartProgramming();
        }
    };

    sendAuthenticationOrStartProgramming = () => {
        if (this.fwuPackageData.authentication?.f) {
            this.sendAuthenticationRequest();
        } else {
            this.sendStartProgrammingRequest();
        }
    };

    sendRequestAndSubscribeOnReport = ({blockData, onReport, responseTimeoutMs}) => {
        const {f, v, n} = blockData;
        const onSuccess = (value) => {
            const isValueValid = v ? this.isValidResponse(value, v) : true;
            const onValidResponse = () => {
                this.sendStatusMessage(n);
                onReport();
            };

            if (isValueValid) {
                onValidResponse();
            } else if (this.fwuPackageData.status_polling) {
                this.startPolling(onValidResponse);
            } else {
                log.debug(`SblFwuClient: response frame is not valid: ${value}`);
                this.onHidFwuError({errorFrame: value});
            }
        };

        framesProcessingService.writeFrameAndPublishResponse({
            frames: [f],
            onSuccess,
            onError: this.onFwuFail,
            responseTimeoutMs,
            doNotSplit: true,
        });
    };

    isValidResponse = (response, validValue) => response.toLowerCase().includes(validValue.toLowerCase());

    sendSuspendMasterCommunication = () => {
        if (this.isMaxTriesExceeded()) return;
        this.currentEventType = firmwareSteps.SUSPEND_MASTER_COMMUNICATION;
        log.info(`SblFwuClient: send sendSuspendMasterCommunication`);

        this.sendRequestAndSubscribeOnReport({
            blockData: this.fwuPackageData.suspend_master_communication,
            onReport: this.sendAuthenticationOrStartProgramming,
        });
    };

    sendAuthenticationRequest = () => {
        if (this.isMaxTriesExceeded()) return;
        this.currentEventType = firmwareSteps.AUTHENTICATION;
        log.info(`SblFwuClient: send sendAuthenticationRequest`);

        this.sendRequestAndSubscribeOnReport({
            blockData: this.fwuPackageData.authentication,
            onReport: this.sendStartProgrammingRequest,
            responseTimeoutMs: hidInputResponseTimeouts.FWU_AUTH,
        });
    };

    sendStartProgrammingRequest = () => {
        if (this.isMaxTriesExceeded()) return;
        this.currentEventType = firmwareSteps.START_PROGRAMMING;
        log.info(`SblFwuClient: send sendStartProgrammingRequest`);

        this.sendRequestAndSubscribeOnReport({
            blockData: this.fwuPackageData.start_programming,
            onReport: this.sendProgramBlocks,
            responseTimeoutMs: hidInputResponseTimeouts.FWU_START_PROGRAMMING,
        });
    };

    sendEndProgrammingRequest = () => {
        this.currentEventType = firmwareSteps.END_PROGRAMMING;
        log.info(`SblFwuClient: send sendEndProgrammingRequest`);

        this.sendRequestAndSubscribeOnReport({
            blockData: this.fwuPackageData.end_programming,
            onReport: this.onFwuFinishBase,
            responseTimeoutMs: hidInputResponseTimeouts.FWU_END_PROGRAMMING,
        });
    };

    sendProgramBlocks = (blockIndex = 0) => {
        if (this.isMaxTriesExceeded()) return;
        this.currentEventType = firmwareSteps.PROGRAMMING;
        log.info(`SblFwuClient: send sendProgramBlocks`);

        this.sendProgramBlock(blockIndex);
    };

    onProgramBlockInputReport = (value) => {
        if (this.isStopped) return;

        const {program_blocks} = this.fwuPackageData;
        const {v, n} = program_blocks[this.currentBlockIndex];
        const isValueValid = this.isValidResponse(value, v);
        const sendNextProgramBlock = () => this.sendProgramBlock(this.currentBlockIndex + 1);

        if (isValueValid) {
            this.sendStatusMessage(n);

            if (this.currentBlockIndex >= program_blocks.length - 1) {
                this.sendEndProgrammingRequest();
            } else {
                sendNextProgramBlock();
            }
        } else if (this.fwuPackageData.status_polling) {
            this.startPolling(() => {
                this.sendProgramBlock(this.currentBlockIndex);
            }, value);
        } else {
            this.onHidFwuError({errorFrame: value, programBlockResponse: value});
        }
    };

    sendProgramBlock = (blockIndex) => {
        this.currentBlockIndex = blockIndex;
        const {f} = this.fwuPackageData.program_blocks[blockIndex];

        framesProcessingService.writeFrameAndPublishResponse({
            frames: [f],
            onSuccess: (frame) => {
                this.onProgramBlockInputReport(frame);
            },
            onError: this.onFwuFail,
            isDebug: false,
            responseTimeoutMs: hidInputResponseTimeouts.FWU_PROGRAM_BLOCKS,
            doNotSplit: true,
        });
    };

    startPolling = (onPollingSuccess, programBlockResponse) => {
        log.info(`SblFwuClient: start polling`);

        this.sendPollingRequestAndSubscribeOnReport(onPollingSuccess, programBlockResponse);
    };

    sendPollingRequestAndSubscribeOnReport = (onPollingSuccess, programBlockResponse) => {
        const {busy, counter, delay, read, success} = this.fwuPackageData.status_polling;

        if (this.pollingTryNumber < counter) {
            this.pollingTryNumber = this.pollingTryNumber + 1;
            const onSuccess = (value) => {
                const isSuccess = this.isValidResponse(value, success);

                if (isSuccess) {
                    this.pollingTryNumber = 0;
                    log.info(`SblFwuClient: polling result - success`);

                    helpers.runFunction(onPollingSuccess);
                } else {
                    const isBusy = this.isValidResponse(value, busy);

                    if (isBusy) {
                        log.info(`SblFwuClient: polling result - busy`);

                        this.pollingTimeout = setTimeout(() => {
                            this.sendPollingRequestAndSubscribeOnReport(onPollingSuccess, programBlockResponse);
                        }, delay);
                    } else {
                        log.info(`SblFwuClient: polling result - fail`);

                        this.onHidFwuError({errorFrame: value, programBlockResponse});
                    }
                }
            };

            framesProcessingService.writeFrameAndPublishResponse({
                frames: [read],
                onSuccess,
                onError: this.onFwuFail,
                doNotSplit: true,
            });
        } else {
            log.info(`SblFwuClient: polling tries exceeded`);

            this.onFwuFail(programBlockResponse);
        }
    };

    onFwuFail = (programBlockResponse) => {
        this.onHidFwuError({programBlockResponse});
    };

    onHidFwuError = ({errorCode = firmwareErrorCodes.GENERAL, errorFrame, programBlockResponse}) => {
        this.stop();

        const errorData = {errorCode, onError: this.onFwuError, errorFrame};
        const errorDetails = fwuErrorDetailsService.getErrorDetails({
            allRequests: this.fwuPackageData.program_blocks,
            currentIndex: this.currentBlockIndex,
            response: programBlockResponse,
        });

        this.onFwuErrorBase({...errorData, ...errorDetails});
    };
}
